import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  subWeeks,
  subMonths,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import { DateRange } from "react-day-picker";

interface DateRangePickerProps {
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  className?: string;
}

// Standard preset list used across every report in Keystone.
// Edit here only — every page consumes this component.
type PresetKey =
  | "week_to_date"
  | "last_week"
  | "this_month"
  | "last_month"
  | "last_3_months"
  | "last_6_months"
  | "last_12_months"
  | "custom";

const PRESETS: Array<{ key: PresetKey; label: string }> = [
  { key: "week_to_date", label: "This week to date" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_3_months", label: "Last 3 months" },
  { key: "last_6_months", label: "Last 6 months" },
  { key: "last_12_months", label: "Last 12 months" },
  { key: "custom", label: "Custom range" },
];

// All presets honour Europe/London week boundaries (Monday start).
function presetRange(key: PresetKey, now = new Date()): DateRange | undefined {
  switch (key) {
    case "week_to_date":
      return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfDay(now) };
    case "last_week": {
      const lastWeek = subWeeks(now, 1);
      return {
        from: startOfWeek(lastWeek, { weekStartsOn: 1 }),
        to: endOfWeek(lastWeek, { weekStartsOn: 1 }),
      };
    }
    case "this_month":
      return { from: startOfMonth(now), to: endOfDay(now) };
    case "last_month": {
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    case "last_3_months":
      return { from: startOfDay(subMonths(now, 3)), to: endOfDay(now) };
    case "last_6_months":
      return { from: startOfDay(subMonths(now, 6)), to: endOfDay(now) };
    case "last_12_months":
      return { from: startOfDay(subMonths(now, 12)), to: endOfDay(now) };
    default:
      return undefined;
  }
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);

  const handlePreset = (key: PresetKey) => {
    if (key === "custom") {
      setShowCustom(true);
      return;
    }
    const range = presetRange(key);
    if (range) {
      onDateRangeChange(range);
      setShowCustom(false);
      setOpen(false);
    }
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setShowCustom(false); }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "justify-start text-left font-normal",
              !dateRange && "text-muted-foreground"
            )}
            data-testid="button-date-range"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "LLL dd, y")} -{" "}
                  {format(dateRange.to, "LLL dd, y")}
                </>
              ) : (
                format(dateRange.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            <div className="flex flex-col border-r p-2 min-w-[180px]">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => handlePreset(p.key)}
                  className="text-left text-sm rounded-md px-3 py-1.5 hover:bg-accent hover:text-accent-foreground transition-colors"
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {showCustom && (
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={(range) => {
                  onDateRangeChange(range);
                  if (range?.from && range?.to) setOpen(false);
                }}
                numberOfMonths={2}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
